import type { Cart } from '@iheartjane/data-access';
import { removeCartProduct } from '@iheartjane/data-access';
import {
  cartKeys,
  useAddLoyaltyRedemption,
  useDeleteCart,
  useRemoveCartProduct,
  useRemoveLoyaltyRedemption,
  useUpdateCart,
  useUpdateCartProduct,
} from '@iheartjane/data-access/react-query';
import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import { EventNames, trackError } from '@iheartjane/tracking';
import type { PriceId } from '@iheartjane/types';
import { useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';

import { useCustomerDispatch } from '@jane/monolith/customer/dispatch';
import {
  applyCrmRedemption,
  attemptToAddToCart,
  checkout,
  deleteCartItem,
  deleteUnavailableProducts,
  removeCrmRedemption,
  resetCart,
  updateCart,
  updateDeliveryAddress,
  updateQueryPromoCode,
  updateReservationMode,
} from '@jane/monolith/customer/redux/cart';
import { selectEmbeddedApp } from '@jane/monolith/customer/redux/embeddedApp';
import { selectStore } from '@jane/monolith/customer/redux/store';
import { useCustomerSelector } from '@jane/monolith/customer/selectors';
import { moeAddToBag, track } from '@jane/shared-ecomm/tracking';
import { postAnalyticsEvent } from '@jane/shared-ecomm/util';
import { getJwt } from '@jane/shared/auth';
import { useEcommApp } from '@jane/shared/providers';
import { getActualUnitPrice } from '@jane/shared/util';

import { getLocalCartUuid, updateLocalCartUuid } from './localCartUuid';

/**
 * The fields a v3 delete needs. Both carts' `unavailable_products` rows satisfy
 * it, so a caller forwards the list it already holds instead of re-deriving ids.
 */
export interface UnavailableProductRef {
  price_id: PriceId;
  product_id: number;
}

/**
 * The write side of the cart facade. Behind `c3UseV3Cart` the seven actions that
 * map one-to-one onto a v3 mutation call it directly and dispatch nothing;
 * flag-off — and, for the five actions still awaiting a v3 body, on both paths —
 * each forwards to the corresponding v1 Redux thunk. The facade name is the
 * canonical v3 name, which differs from the legacy export.
 * TODO(carts-v3): re-point the remaining five actions once their backing exists.
 *
 * The v3 bodies deliberately drop the thunks' `bundlePossibilities` dispatches
 * (`setTouchedProduct`): that is a separate slice whose writes stay on Redux
 * until slot 017, per ADR-0003 — thunk-coupled client state migrates at the flip,
 * not incrementally.
 */
export interface CartFacadeActions {
  /**
   * The consumer-level add. Forwards `attemptToAddToCart`, which owns the
   * store-switch guard, the add-vs-update branch, and the add-to-cart analytics —
   * not the inner `addToCart` thunk (which has none of those). A cart is still
   * created implicitly on first add inside `attemptToAddToCart → addToCart`.
   */
  addProduct: (...args: Parameters<typeof attemptToAddToCart>) => void;
  applyLoyalty: (...args: Parameters<typeof applyCrmRedemption>) => void;
  applyPromoCode: (...args: Parameters<typeof updateQueryPromoCode>) => void;
  checkout: (...args: Parameters<typeof checkout>) => void;
  createCart: () => void;
  deleteUnavailableProducts: (
    products: readonly UnavailableProductRef[]
  ) => void;
  removeLoyalty: (...args: Parameters<typeof removeCrmRedemption>) => void;
  removeProduct: (...args: Parameters<typeof deleteCartItem>) => void;
  resetCart: (...args: Parameters<typeof resetCart>) => void;
  updateDeliveryAddress: (
    ...args: Parameters<typeof updateDeliveryAddress>
  ) => void;
  updateProduct: (...args: Parameters<typeof updateCart>) => void;
  updateReservationMode: (
    ...args: Parameters<typeof updateReservationMode>
  ) => void;
}

export const useCartActions = (): CartFacadeActions => {
  const dispatch = useCustomerDispatch();
  const useV3Cart = useFlag(FLAGS.c3UseV3Cart);
  const { appMode } = useEcommApp();
  const { partnerId: storeId } = useCustomerSelector(selectEmbeddedApp);
  // `storeCurrency` and moe's `storeId`/`storeName` are not on the v3 cart
  // (`Cart.store` is `{ id }`), so both paths take them from the store slice
  // exactly as the thunks do.
  const { store } = useCustomerSelector(selectStore);
  const queryClient = useQueryClient();

  // `mutate` is the stable half of each result object, so the actions memo can
  // depend on it without re-creating on every render.
  const { mutate: mutateUpdateCart } = useUpdateCart();
  const { mutate: mutateDeleteCart } = useDeleteCart();
  const { mutate: mutateUpdateCartProduct } = useUpdateCartProduct();
  const { mutate: mutateRemoveCartProduct } = useRemoveCartProduct();
  const { mutate: mutateAddLoyalty } = useAddLoyaltyRedemption();
  const { mutate: mutateRemoveLoyalty } = useRemoveLoyaltyRedemption();

  return useMemo(() => {
    const v1Actions: CartFacadeActions = {
      addProduct: (...args) => dispatch(attemptToAddToCart(...args)),
      updateProduct: (...args) => dispatch(updateCart(...args)),
      removeProduct: (...args) => dispatch(deleteCartItem(...args)),
      applyPromoCode: (...args) => dispatch(updateQueryPromoCode(...args)),
      updateReservationMode: (...args) =>
        dispatch(updateReservationMode(...args)),
      updateDeliveryAddress: (...args) =>
        dispatch(updateDeliveryAddress(...args)),
      applyLoyalty: (...args) => dispatch(applyCrmRedemption(...args)),
      removeLoyalty: (...args) => dispatch(removeCrmRedemption(...args)),
      // Neither the request body nor the reducer reads the payload, so mapping
      // the rows back to ids is byte-equivalent to what the thunk received.
      deleteUnavailableProducts: (products) =>
        dispatch(
          deleteUnavailableProducts(
            products.map((product) => product.product_id)
          )
        ),
      checkout: (...args) => dispatch(checkout(...args)),
      resetCart: (...args) => dispatch(resetCart(...args)),
      // A cart is created implicitly on first add — there is no standalone create
      // thunk. `createCart` exists on the interface because consumers (and the
      // future v3 backing) expect it, but on the v1 Redux path it is a no-op: the
      // real network create happens inside `addProduct`/`addToCart`. It must NOT
      // dispatch `initiateCreateCart` — that reducer sets `isLoading: true` and the
      // only reducer that clears it is `createHeadlessCartSuccess` (the headless
      // thunk's success). Without an immediately-following headless create, that
      // would strand `isLoading`/`isFetching` stuck true.
      createCart: () => undefined,
    };

    if (!useV3Cart) return v1Actions;

    // A write needs the cart at call time, not reactively, so the snapshot comes
    // from the query cache rather than a second `useCart` observer (which would
    // re-render every consumer of this hook). No uuid or no cached cart is v1's
    // `if (!cart.uuid) return`.
    const cartSnapshot = (uuid: string) =>
      queryClient.getQueryData<Cart>(cartKeys.detail(uuid)) ?? null;

    /**
     * Always invalidate — never predict whether the row we just deleted was the
     * last one. `destroy` answers 204 with no body, so the server's
     * `@cart.destroy! if @cart.unfilled?` is unobservable here, and a snapshot
     * taken before the request races any concurrent removal. A destroyed cart's
     * refetch 404s, which `useCartState` turns into a cleared uuid and an empty
     * bag; that one wasted request buys a decision that cannot be raced.
     */
    const settleAfterRemoval = (uuid: string) =>
      queryClient.invalidateQueries({ queryKey: cartKeys.detail(uuid) });

    return {
      ...v1Actions,

      updateProduct: ({ count, price_id, product_id }, moeTrack) => {
        const uuid = getLocalCartUuid(appMode, storeId);
        const cart = uuid ? cartSnapshot(uuid) : null;
        if (!uuid || !cart) return;

        const cartProduct = cart.products.find(
          (product) =>
            product.id === product_id || product.product_id === product_id
        );
        const changeInCount = (count ?? 0) - (cartProduct?.count ?? 0);

        mutateUpdateCartProduct(
          {
            appMode,
            cartUuid: uuid,
            count,
            jwt: getJwt(),
            priceId: price_id,
            productId: Number(product_id),
          },
          {
            onSuccess: () => {
              if (changeInCount <= 0 || !cartProduct) return;
              moeTrack(
                moeAddToBag({
                  cartUuid: uuid,
                  product: { ...cartProduct, count: changeInCount },
                  storeId: store?.id ?? null,
                  storeName: store?.name ?? null,
                })
              );
            },
          }
        );
      },

      removeProduct: (productId, priceId) => {
        const uuid = getLocalCartUuid(appMode, storeId);
        const cart = uuid ? cartSnapshot(uuid) : null;
        if (!uuid || !cart) return;

        const product = cart.products.find(
          (candidate) =>
            candidate.id === productId && candidate.price_id === priceId
        );
        // v1's predicate, kept for `cartEmptied` parity (`cart.ts:351`). Not a
        // destroy predictor — see `settleAfterRemoval`.
        const wasOnlyLine = cart.products.length === 1;

        if (product) {
          track({
            event: EventNames.RemovedProductFromCart,
            productBrand: product.brand,
            productCategory: product.category,
            productId: productId.toString(),
            productName: product.name,
            // Deliberately no special — the base `price_${priceId}`, as v1 reads.
            productPrice: getActualUnitPrice(product, product.price_id) || 0,
            quantity: product.count,
            storeCurrency: store?.currency_code,
          });
        }

        const basePrice = product ? Number(product[`price_${priceId}`]) : 0;
        const discountedPrice = product
          ? Number(product[`discounted_price_${priceId}`])
          : 0;

        mutateRemoveCartProduct(
          {
            appMode,
            cartUuid: uuid,
            jwt: getJwt(),
            priceId,
            productId: Number(productId),
          },
          {
            onSuccess: () => {
              postAnalyticsEvent({
                name: 'cartItemRemoval',
                properties: {
                  priceId,
                  productId,
                  ...(basePrice > 0 && { price: basePrice }),
                  ...(discountedPrice > 0 && { discountedPrice }),
                },
              });

              if (wasOnlyLine) postAnalyticsEvent({ name: 'cartEmptied' });

              settleAfterRemoval(uuid);
            },
          }
        );
      },

      updateReservationMode: ({ reservationMode, userGroupSpecialId }) => {
        const uuid = getLocalCartUuid(appMode, storeId);
        if (!uuid) return;

        // Not dual-write: the write and the read are both v3. A controlled
        // toggle cannot wait on the round-trip, so patch the cache and roll the
        // snapshot back if the PATCH rejects.
        const queryKey = cartKeys.detail(uuid);
        // An in-flight refetch would resolve with the pre-toggle mode and clobber
        // the patch below — likelier now that the cart is never fresh. Not
        // awaited: the abort is synchronous, and the toggle is controlled, so
        // deferring the patch past the click would drop a frame.
        queryClient.cancelQueries({ queryKey });
        const previousCart = queryClient.getQueryData<Cart>(queryKey);
        if (previousCart) {
          queryClient.setQueryData<Cart>(queryKey, {
            ...previousCart,
            reservation_mode: reservationMode,
          });
        }

        mutateUpdateCart(
          {
            appMode,
            jwt: getJwt(),
            reservationMode,
            userGroupSpecialId,
            uuid,
          },
          {
            onError: () => {
              // The snapshot restores immediately, but under a double-toggle it
              // is itself an unconfirmed optimistic value, so reconcile with the
              // server rather than trusting it. Success needs no invalidate —
              // `useUpdateCart` writes the response cart.
              if (previousCart) {
                queryClient.setQueryData(queryKey, previousCart);
              }
              queryClient.invalidateQueries({ queryKey });
            },
          }
        );
      },

      applyLoyalty: (crmParams) => {
        const uuid = getLocalCartUuid(appMode, storeId);
        if (!uuid) return;

        // Only the nine keys the v3 controller permits; the rest of the v1
        // redemption object is dropped by the endpoint's own contract.
        mutateAddLoyalty({
          appMode,
          cartUuid: uuid,
          crmMemberPoints: crmParams.crm_member_points,
          crmProvider: crmParams.crm_provider,
          crmRewardId: String(crmParams.crm_reward_id),
          jwt: getJwt(),
          pointValue: crmParams.point_value,
          redemptionChecksum: crmParams.redemption_checksum,
          redemptionUrl: crmParams.redemption_url,
          reward: crmParams.reward,
          rewardName: crmParams.reward_name,
        });
      },

      // `crmProvider` is unused on v3 — `destroy` finds the redemption by id alone.
      removeLoyalty: (id) => {
        const uuid = getLocalCartUuid(appMode, storeId);
        if (!uuid) return;

        mutateRemoveLoyalty({
          appMode,
          cartUuid: uuid,
          jwt: getJwt(),
          redemptionId: Number(id),
        });
      },

      deleteUnavailableProducts: (products) => {
        const uuid = getLocalCartUuid(appMode, storeId);
        if (!uuid || !products.length) return;

        // The caller's rows are authoritative, not a cache snapshot: both paths
        // into this modal refresh the v1 slice via a thunk that never invalidates
        // `['cart', uuid]`, so a snapshot can still report none unavailable.
        const jwt = getJwt();

        // One terminal cache write, not one per delete — which is why this uses
        // the raw API fn rather than `useRemoveCartProduct` (that hook
        // invalidates on every success). `allSettled` so one rejection does not
        // strand the rest.
        Promise.allSettled(
          products.map((product) =>
            removeCartProduct({
              appMode,
              cartUuid: uuid,
              jwt,
              priceId: product.price_id,
              productId: product.product_id,
            })
          )
        ).then((results) => {
          // v1 cleared every unavailable row in one atomic request, so a partial
          // failure is new here. The refetch below re-raises the alert with
          // whatever survived, but nothing else would record why.
          const rejected = results.filter(
            (result) => result.status === 'rejected'
          );
          if (rejected.length) {
            trackError(
              new Error('Failed to delete unavailable cart products'),
              {
                cartUuid: uuid,
                failed: rejected.length,
                reasons: rejected.map((result) => String(result.reason)),
                requested: products.length,
              }
            );
          }

          settleAfterRemoval(uuid);
        });
      },

      resetCart: () => {
        const uuid = getLocalCartUuid(appMode, storeId);
        if (!uuid) return;

        mutateDeleteCart(
          { appMode, jwt: getJwt(), uuid },
          { onSuccess: () => updateLocalCartUuid(appMode, storeId, null) }
        );
      },
    };
  }, [
    appMode,
    dispatch,
    mutateAddLoyalty,
    mutateDeleteCart,
    mutateRemoveCartProduct,
    mutateRemoveLoyalty,
    mutateUpdateCart,
    mutateUpdateCartProduct,
    queryClient,
    store,
    storeId,
    useV3Cart,
  ]);
};
